import React from 'react';
import ButtonColorsEnum from '../Enums/ButtonColorsEnum';
import ButtonTypeEnum from '../Enums/ButtonTypeEnum';
import IconPositionEnum from '../Enums/IconPositionEnum';
import ShownPanelsEnum from '../Enums/ShownPanelsEnum';
import AuthService from '../Services/authService';
import RoleService from '../Services/roleService';
import FigmaButton from './FigmaButton';

// A page-level action cluster floats into the page-header band's top-right corner,
// sharing the band with the page title. A long row is wider than the space the title
// leaves, so it overflowed to the left — under the sidebar — and on a phone it was
// unreachable altogether. Measure the row against that space and, when it does not fit,
// collapse it behind a hamburger toggle that opens it as a dropdown: the same menu
// language the detail popups use for their own action row (the "…" toggle in
// TabList/OverWindow + the .ow-actions-open block in Style.css).
const LIST_PAGE_CLASS = 'MenuButtonList';
// Full detail pages (TabbedPage — Fraud Hit, IOB) float their page-level actions with the
// popup row's class instead. Inside a popup that same row is surfaced by the popup
// header's own "…", so it must not grow a second toggle — hence the ancestry check.
const DETAIL_PAGE_CLASS = 'InnerMenuButtonFigmaList';
const POPUP_SELECTOR = '.top-of-boxFigma';
// Breathing room between the title and the row, plus the cluster's own right inset.
const CLUSTER_GUTTER = 48;

class ButtonRow extends React.Component {
  static defaultProps = {
    colors: [],
    labels: [],
    keys: [],
    icons: [],
    roles: [],
    href: [],
    isItCreate: [],
    roleKeys: null,
    onClick: () => {},
    value: ShownPanelsEnum.None,
    disabled: null,
    buttonTypes: [],
    iconPositions: [],
    className: '',
    buttonClassNames: [],
  };

  constructor(props) {
    super(props);
    this.state = {
      active: ShownPanelsEnum.None,
      // A list page's cluster is known from the class alone, so it can lay out at its
      // natural width from the very first render; a detail page's needs the popup check,
      // which only works once mounted (see isCollapsible).
      collapsible: this.wrapperClassName().split(' ').indexOf(LIST_PAGE_CLASS) >= 0,
      collapsed: false,
      actionsOpen: false,
    };

    this.rootRef = React.createRef();
    this.toggleRef = React.createRef();
    this.naturalWidth = 0;
    this.buttonSignature = '';

    this.measure = this.measure.bind(this);
    this.toggleActions = this.toggleActions.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
  }

  componentDidMount() {
    if (!this.isCollapsible()) return;
    // React flushes a setState from componentDidMount before the browser paints, so the
    // detail-page cluster gets its natural-width layout without a visible reflow.
    if (this.state.collapsible) this.measure();
    else this.setState({ collapsible: true }, this.measure);
    window.addEventListener('resize', this.measure, false);
    document.addEventListener('click', this.handleDocumentClick, false);
    const content = this.rootRef.current ? this.rootRef.current.closest('.Basecontent') : null;
    if (content && typeof ResizeObserver !== 'undefined') {
      this.contentObserver = new ResizeObserver(this.measure);
      this.contentObserver.observe(content);
    }
    // Button widths only settle once the web fonts swap in (Oswald is narrower than the
    // fallback), so the first measurement can read wider than the row really is.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(this.measure).catch(() => {});
    }
  }

  componentDidUpdate() {
    this.measure();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.measure, false);
    document.removeEventListener('click', this.handleDocumentClick, false);
    if (this.contentObserver) this.contentObserver.disconnect();
  }

  wrapperClassName() {
    return this.props.className === '' ? LIST_PAGE_CLASS : this.props.className;
  }

  // Only callable once mounted — the popup check needs the rendered node. Everything that
  // reads it (componentDidMount, measure) runs after the first render, and the toggle is
  // rendered off this.state.collapsed, which only measure() can set.
  isCollapsible() {
    const classes = this.wrapperClassName().split(' ');
    if (classes.indexOf(LIST_PAGE_CLASS) >= 0) return true;
    if (classes.indexOf(DETAIL_PAGE_CLASS) < 0) return false;
    const root = this.rootRef.current;
    return !!root && !root.closest(POPUP_SELECTOR);
  }

  // Space left for the cluster in the row it shares with the page title. On desktop the
  // title is content-sized, so a short title leaves the row plenty of room; below 768px
  // the title is flex:1 1 auto and claims the band, which collapses the row on phones
  // without needing a breakpoint of its own.
  availableWidth() {
    const root = this.rootRef.current;
    const content = root ? root.closest('.Basecontent') : null;
    const width = content ? content.clientWidth : window.innerWidth;
    const title = document.querySelector('.pageheader .page-title');
    const titleWidth = title ? title.getBoundingClientRect().width : 0;
    return width - titleWidth - CLUSTER_GUTTER;
  }

  measure() {
    if (!this.state.collapsible) return;
    const root = this.rootRef.current;
    if (!root) return;
    const group = root.querySelector('.btn-group');
    if (!group) return;

    const signature = `${this.props.keys.join('|')}::${this.props.labels.join('|')}`;
    if (signature !== this.buttonSignature) {
      this.buttonSignature = signature;
      this.naturalWidth = 0;
    }

    // The row is only measurable while it is laid out horizontally — once collapsed it is
    // a stacked dropdown — so keep the last natural width and re-decide against that. The
    // .pa-actions width:max-content is what makes this reading honest: a shrink-to-fit
    // cluster squeezes its buttons instead of overflowing, hiding the overflow we test for.
    if (!this.state.collapsed) {
      const natural = group.getBoundingClientRect().width;
      if (natural > 0) this.naturalWidth = natural + CLUSTER_GUTTER;
    }

    if (!this.naturalWidth) {
      // The button set changed while collapsed: expand once so the next pass can measure.
      if (this.state.collapsed) this.setState({ collapsed: false, actionsOpen: false });
      return;
    }

    const collapsed = this.naturalWidth > this.availableWidth();
    if (collapsed !== this.state.collapsed) {
      this.setState({ collapsed, actionsOpen: false });
    }
  }

  toggleActions() {
    this.setState((prev) => ({ actionsOpen: !prev.actionsOpen }));
  }

  // Any click outside the toggle closes the menu — including clicks on the action buttons
  // themselves (their own handlers fire first via React's root listener; this
  // document-level listener runs after).
  handleDocumentClick(event) {
    if (!this.state.actionsOpen) return;
    const toggle = this.toggleRef.current;
    if (toggle && toggle.contains(event.target)) return;
    this.setState({ actionsOpen: false });
  }

  renderButton(
    label,
    key,
    iconName,
    index,
    disabled = false,
    color = ButtonColorsEnum.Common,
    buttonType,
    iconPosition,
    href = '',
    tooltip = '',
    buttonClassName = ''
  ) {
    iconPosition = iconPosition ? iconPosition : iconName ? IconPositionEnum.Left : undefined;
    const button = (
      <FigmaButton
        className={`${buttonClassName}`}
        type={
          buttonType
            ? buttonType
            : iconName
            ? label
              ? ButtonTypeEnum.NormalIconLeft
              : ButtonTypeEnum.NormalIconOnly
            : ButtonTypeEnum.NormalNormal
        }
        label={label}
        color={color}
        iconName={iconName}
        disabled={disabled}
        onClick={() => this.props.onClick(key)}
        iconPosition={iconPosition}
        key={`${key}${index}`}
        tooltip={tooltip}
      />
    );
    if (href !== '') {
      return (
        <a
          href={href}
          target='_blank'
          rel='noopener noreferrer'
          key={`a${key}${index}`}
        >
          {button}
        </a>
      );
    }
    return button;
  }

  render() {
    const loggedInUser = AuthService.getInstance().GetLoggedInUser();
    const buttons = [];
    this.props.keys.forEach((k, index) => {
      let hasPermission = false;
      if (this.props.roleKeys != null && Array.isArray(this.props.roleKeys) && this.props.roleKeys.length > 0) {
        hasPermission =
          this.props.roleKeys[index] === '' ||
          RoleService.CheckUserPermission(
            'ButtonRowButton',
            this.props.roleKeys[index] ? this.props.roleKeys[index] : ''
          );
      } else if (this.props.roles != null && Array.isArray(this.props.roles) && this.props.roles.length > 0) {
        hasPermission =
          this.props.roles[index] === '' || RoleService.CheckUserPermission('ButtonRowButton', this.props.roles[index]);
      } else {
        hasPermission =
          this.props.roles.length === 0 ||
          this.props.roles[index] === 'User' ||
          loggedInUser.roles.includes(this.props.roles[index]);
      }
      if (hasPermission) {
        buttons.push(
          this.renderButton(
            this.props.labels[index],
            k,
            this.props.icons[index],
            index,
            this.props.disabled !== null && this.props.disabled[index],
            this.props.colors[index],
            this.props.buttonTypes[index],
            this.props.iconPositions[index],
            this.props.href[index],
            this.props.tooltips ? this.props.tooltips[index] : '',
            this.props.buttonClassNames[index] ? this.props.buttonClassNames[index] : ''
          )
        );
      }
    });

    const collapsed = this.state.collapsed;
    const classNames = [this.wrapperClassName()];
    if (this.state.collapsible) classNames.push('pa-actions');
    if (collapsed) classNames.push('pa-collapsed');
    if (collapsed && this.state.actionsOpen) classNames.push('pa-open');

    return (
      <div
        className={classNames.join(' ')}
        ref={this.rootRef}
      >
        {collapsed && (
          <button
            type='button'
            className='pa-actions-toggle'
            title='Actions'
            aria-label='Actions'
            aria-expanded={this.state.actionsOpen}
            ref={this.toggleRef}
            onClick={this.toggleActions}
          >
            <i className='fa-solid fa-bars' />
          </button>
        )}
        <div className='m-1 btn-group '>{buttons}</div>
      </div>
    );
  }
}

export default ButtonRow;
